import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { api, unwrap } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Skeleton } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { DataTable, EmptyRow, Field, RootError } from '@/components/ui/form-controls';
import { Input, NativeSelect } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { tenantKeys, useTenants } from './api';
import { createTenantSchema, parseLocales, type CreateTenantForm } from './schemas';

export function TenantsPage() {
  const { t } = useTranslation();
  const tenants = useTenants();
  const [open, setOpen] = useState(false);

  return (
    <div>
      <PageHeader
        title={t('admin.tenants.title')}
        description={t('admin.tenants.description')}
        help="tenants"
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus /> {t('admin.tenants.create')}
          </Button>
        }
      />
      <div className="p-6">
        <DataTable head={[t('admin.tenants.name'), t('admin.tenants.slug'), t('admin.tenants.timezone'), t('admin.tenants.locales'), t('admin.tenants.jde'), t('admin.tenants.status')]}>
          {tenants.isPending && (
            <tr>
              <td colSpan={6}>
                <Skeleton className="h-6 w-full" />
              </td>
            </tr>
          )}
          {tenants.data?.items.length === 0 && <EmptyRow colSpan={6}>{t('admin.tenants.empty')}</EmptyRow>}
          {tenants.data?.items.map((tn) => (
            <tr key={tn.id} className="hover:bg-muted/40">
              <td>
                <Link to={`/tenants/${tn.id}`} className="font-medium text-accent hover:underline">
                  {tn.name}
                </Link>
              </td>
              <td className="font-mono text-xs">{tn.slug}</td>
              <td>{tn.timezone}</td>
              <td>{tn.locales.join(', ')}</td>
              <td>
                {tn.jde.verifiedAt ? (
                  <Badge variant="success">{t('admin.jde.verified')}</Badge>
                ) : (
                  <Badge variant="warning">{t('admin.jde.notVerified')}</Badge>
                )}
              </td>
              <td>
                <Badge variant={tn.status === 'active' ? 'default' : 'destructive'}>{t(`admin.tenantStatus.${tn.status}`)}</Badge>
              </td>
            </tr>
          ))}
        </DataTable>
      </div>
      <CreateTenantDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function CreateTenantDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const form = useForm<CreateTenantForm>({
    resolver: zodResolver(createTenantSchema(t)),
    defaultValues: { name: '', slug: '', timezone: 'Europe/Zurich', locales: 'de, fr', residency: 'any' },
  });
  const create = useMutation({
    meta: { silent: true },
    mutationFn: async (v: CreateTenantForm) => {
      const res = await api.POST('/v1/tenants', {
        body: { name: v.name, slug: v.slug, timezone: v.timezone, locales: parseLocales(v.locales), residency: v.residency },
      });
      return unwrap(res) as unknown as { id?: string } | undefined;
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const created = await create.mutateAsync(values);
      await qc.invalidateQueries({ queryKey: tenantKeys.list() });
      onOpenChange(false);
      form.reset();
      if (created?.id) void navigate(`/tenants/${created.id}`);
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['name', 'slug', 'timezone', 'locales', 'residency'] });
    }
  });
  const { errors, isSubmitting } = form.formState;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>{t('admin.tenants.create')}</DialogTitle>
        <DialogDescription>{t('admin.tenants.createHint')}</DialogDescription>
        <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field label={t('admin.tenants.name')} htmlFor="tn-name" error={errors.name?.message}>
            <Input id="tn-name" aria-invalid={!!errors.name} {...form.register('name')} />
          </Field>
          <Field label={t('admin.tenants.slug')} htmlFor="tn-slug" error={errors.slug?.message} hint={t('admin.tenants.slugHint')}>
            <Input id="tn-slug" className="font-mono" aria-invalid={!!errors.slug} {...form.register('slug')} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('admin.tenants.timezone')} htmlFor="tn-tz" error={errors.timezone?.message}>
              <Input id="tn-tz" aria-invalid={!!errors.timezone} {...form.register('timezone')} />
            </Field>
            <Field label={t('admin.tenants.residency')} htmlFor="tn-res">
              <NativeSelect id="tn-res" {...form.register('residency')}>
                <option value="any">{t('admin.residency.any')}</option>
                <option value="eu">{t('admin.residency.eu')}</option>
              </NativeSelect>
            </Field>
          </div>
          <Field label={t('admin.tenants.locales')} htmlFor="tn-loc" error={errors.locales?.message} hint={t('admin.tenants.localesHint')}>
            <Input id="tn-loc" aria-invalid={!!errors.locales} {...form.register('locales')} />
          </Field>
          <RootError message={errors.root?.message} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('admin.common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {t('admin.common.create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
